import { PropsWithChildren } from 'react'
import {
  createInstance,
  logOnlyEventDispatcher,
  OptimizelyProvider
} from '@optimizely/react-sdk'
import { COOKIE_KEY_FCID, getCookie } from 'utils/cookie'
import { getOptimizelyTrackingAttribute } from 'utils/tracking'
import optimizelyErrorHandler from 'utils/tracking/optimizely/errorHandler'
import isNotAffiliate from 'utils/formDependencies/conditions/isNotAffiliate'
import { useData } from 'stores/utils/hooks/useData'
import { useUrlParams } from 'stores/utils/hooks/useUrlParams'
import { isApp } from 'utils/formDependencies/conditions/isApp'
import { isE2ETesting } from 'utils/test/isE2ETesting'

// Relies on continous internet connection in contrast to the datafile approach
export const optimizelyClient = createInstance({
  sdkKey: process.env.REACT_APP_OPTIMIZELY_SDK_KEY,
  errorHandler: optimizelyErrorHandler,
  eventDispatcher: logOnlyEventDispatcher
})

export enum Experiments {
  CAT_3614_CAPTCHA_KILL_SWITCH = 'cat-3614_kill_switch',
  CAT_3494_DAC_KILL_SWITCH = '2025-07-25-cat-dac-kill-switch-flag',
  CAT_4149_EARLIER_LEAD_CREATION = '2026-02-18-cat-4149-earlier-lead-creation',
  CAT_4197_PREFILL_V2 = '2026-03-18-cat-4197-prefill-v2',
  CAT_4291_DAC_SKIP_BUTTON_V3 = '2026-04-08-cat-4291-dac-skip-button-v3',
  // AA tests
  CAT_3752_AA_TEST_YOUNG_AUDIENCE = '2025-08-28-cat-segmentation-aa-test_young',
  CAT_3752_AA_TEST_MIDDLE_AUDIENCE = '2025-08-28-cat-segmentation-aa-test_middle',
  CAT_3752_AA_TEST_OLD_AUDIENCE = '2025-08-28-cat-segmentation-aa-test_old',
  CAT_4251_AA_TEST_NEW_EVENT = '2026-03-11-cat-aa-test-new-event_v3'
}

const CustomOptimizelyProvider = ({ children }: PropsWithChildren<unknown>) => {
  const urlParams = useUrlParams()

  const { data } = useData()

  const isOptimizelyDisabled =
    !isNotAffiliate(urlParams?.context) ||
    isApp(urlParams?.isAppContext) ||
    isE2ETesting()

  if (isOptimizelyDisabled) {
    return <>{children}</>
  }

  const userId = getCookie(COOKIE_KEY_FCID) || ''

  optimizelyClient.setUser({
    id: userId,
    attributes: { ...getOptimizelyTrackingAttribute(data) }
  })

  optimizelyClient
    .onReady()
    .then(() => (
      <OptimizelyProvider optimizely={optimizelyClient}>
        {children}
      </OptimizelyProvider>
    ))

  return <>{children}</>
}

export default CustomOptimizelyProvider
